/**
 * Voice Recorder Hook
 * Hook for recording audio messages using MediaRecorder API
 */

import { useState, useRef, useCallback, useEffect } from 'react';

export type RecordingState = 'idle' | 'recording' | 'paused' | 'stopped';

interface UseVoiceRecorderResult {
  state: RecordingState;
  duration: number; // in seconds
  audioLevel: number; // 0-1 normalized audio level for visualization
  error: string | null;
  audioBlob: Blob | null;
  audioUrl: string | null;
  startRecording: (preAcquiredStream?: MediaStream) => Promise<void>;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  cancelRecording: () => void;
  resetRecording: () => void;
}

const MAX_DURATION = 300; // 5 minutes
const TIMER_INTERVAL_MS = 100;
const AUDIO_LEVEL_INTERVAL_MS = 50;

/**
 * Hook for recording voice messages
 */
export const useVoiceRecorder = (): UseVoiceRecorderResult => {
  const [state, setState] = useState<RecordingState>('idle');
  const [duration, setDuration] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedDurationRef = useRef<number>(0);
  const pauseStartRef = useRef<number>(0);
  const isStartingRef = useRef(false);
  const isCancelledRef = useRef(false);

  // Audio level analysis refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioLevelTimerRef = useRef<number | null>(null);

  const cleanupAudioAnalyser = useCallback(() => {
    if (audioLevelTimerRef.current) {
      clearInterval(audioLevelTimerRef.current);
      audioLevelTimerRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
    }
    audioContextRef.current = null;
    analyserRef.current = null;
    setAudioLevel(0);
  }, []);

  const cleanupExistingRecorder = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    cleanupAudioAnalyser();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.ondataavailable = null;
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current.onerror = null;
        mediaRecorderRef.current.stop();
      } catch {
        // ignore
      }
    }
    mediaRecorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    chunksRef.current = [];
  }, [cleanupAudioAnalyser]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanupExistingRecorder();
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setupAudioAnalyser = useCallback((stream: MediaStream) => {
    try {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      audioLevelTimerRef.current = setInterval(() => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        // Compute average level normalized to 0-1
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i];
        }
        const avg = sum / dataArray.length / 255;
        setAudioLevel(avg);
      }, AUDIO_LEVEL_INTERVAL_MS) as unknown as number;
    } catch {
      // Audio analyser is optional — recording still works without it
    }
  }, []);

  /**
   * Stop recording
   */
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  /**
   * Start recording
   */
  const startRecording = useCallback(async (preAcquiredStream?: MediaStream) => {
    // Guard against double-invocation (React StrictMode, rapid clicks)
    if (isStartingRef.current) return;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') return;
    isStartingRef.current = true;
    isCancelledRef.current = false;

    try {
      setError(null);

      // Clean up any previous recorder/stream before starting fresh
      cleanupExistingRecorder();

      // Use pre-acquired stream if provided (eliminates ~1s mic warmup delay),
      // otherwise acquire a new one
      let stream: MediaStream;
      if (preAcquiredStream) {
        stream = preAcquiredStream;
      } else {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Вашият браузър не поддържа запис на аудио');
        }
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          }
        });

        // If cancelled while waiting for getUserMedia permission
        if (isCancelledRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          isStartingRef.current = false;
          return;
        }
      }

      streamRef.current = stream;

      // Determine supported MIME type (prefer opus codec for better quality)
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          if (MediaRecorder.isTypeSupported('audio/ogg')) {
            mimeType = 'audio/ogg';
          } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
            mimeType = 'audio/mp4';
          } else {
            mimeType = '';
          }
        }
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType || undefined,
      });
      mediaRecorderRef.current = mediaRecorder;

      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // If cancelled, don't create blob/url
        if (isCancelledRef.current) return;

        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        setAudioBlob(blob);

        const url = URL.createObjectURL(blob);
        setAudioUrl(url);

        setState('stopped');

        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        cleanupAudioAnalyser();

        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        setError('Грешка при записа на аудио');
        cleanupExistingRecorder();
        setState('idle');
      };

      // Start without timeslice — single chunk on stop() with correct WebM
      // duration metadata (timeslice creates separate segments that break
      // playback duration in many browsers)
      mediaRecorder.start();
      setState('recording');

      // Setup audio level analyser for visualization
      setupAudioAnalyser(stream);

      // Start timer
      startTimeRef.current = Date.now();
      pausedDurationRef.current = 0;
      pauseStartRef.current = 0;
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current - pausedDurationRef.current) / 1000);
        setDuration(elapsed);

        if (elapsed >= MAX_DURATION) {
          stopRecording();
        }
      }, TIMER_INTERVAL_MS) as unknown as number;

    } catch (err) {
      console.error('Error starting recording:', err);

      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('Разрешете достъп до микрофона за да записвате гласови съобщения');
        } else if (err.name === 'NotFoundError') {
          setError('Не е намерен микрофон');
        } else {
          setError(err.message);
        }
      } else {
        setError('Грешка при стартиране на записа');
      }

      setState('idle');
    } finally {
      isStartingRef.current = false;
    }
  }, [cleanupExistingRecorder, cleanupAudioAnalyser, setupAudioAnalyser, stopRecording]);

  /**
   * Pause recording
   */
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setState('paused');

      // Record when pause started
      pauseStartRef.current = Date.now();

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Pause audio level updates
      if (audioLevelTimerRef.current) {
        clearInterval(audioLevelTimerRef.current);
        audioLevelTimerRef.current = null;
      }
      setAudioLevel(0);
    }
  }, []);

  /**
   * Resume recording
   */
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setState('recording');

      // Accumulate paused duration
      if (pauseStartRef.current > 0) {
        pausedDurationRef.current += Date.now() - pauseStartRef.current;
        pauseStartRef.current = 0;
      }

      // Resume timer
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current - pausedDurationRef.current) / 1000);
        setDuration(elapsed);

        if (elapsed >= MAX_DURATION) {
          stopRecording();
        }
      }, TIMER_INTERVAL_MS) as unknown as number;

      // Resume audio level analyser
      if (analyserRef.current) {
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        audioLevelTimerRef.current = setInterval(() => {
          if (!analyserRef.current) return;
          analyserRef.current.getByteFrequencyData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i];
          }
          const avg = sum / dataArray.length / 255;
          setAudioLevel(avg);
        }, AUDIO_LEVEL_INTERVAL_MS) as unknown as number;
      }
    }
  }, [stopRecording]);

  /**
   * Cancel recording (discard)
   */
  const cancelRecording = useCallback(() => {
    isCancelledRef.current = true;
    cleanupExistingRecorder();

    setAudioBlob(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setDuration(0);
    setAudioLevel(0);
    setState('idle');
    setError(null);
  }, [audioUrl, cleanupExistingRecorder]);

  /**
   * Reset after sending
   */
  const resetRecording = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setAudioBlob(null);
    setDuration(0);
    setAudioLevel(0);
    setState('idle');
    setError(null);
    chunksRef.current = [];
  }, [audioUrl]);

  return {
    state,
    duration,
    audioLevel,
    error,
    audioBlob,
    audioUrl,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    resetRecording,
  };
};
